<template>
<div class="dashboard-shell">
  <div class="dashboard-background"></div>
  <navbar/>
  <main class="main-frame">
    <aside class="dashboard-menu-component">
      <Menu />
    </aside>
    <section class="page-router-component" :aria-busy="requestLoading ? 'true' : 'false'">
      <transition name="dashboard-loading-fade">
        <div
          v-if="requestLoading"
          class="dashboard-loading-indicator"
          role="status"
          aria-live="polite"
        >
          <span class="dashboard-loading-spinner" aria-hidden="true">
            <span class="dashboard-loading-spinner__circle dashboard-loading-spinner__circle--top"></span>
            <span class="dashboard-loading-spinner__circle dashboard-loading-spinner__circle--bottom"></span>
          </span>
          <span>Loading</span>
        </div>
      </transition>
      <router-view></router-view>
    </section>
  </main>
</div>
</template>

<script>
import navbar         from '../navbar.vue'
import axios          from 'axios'
import {SERVER_PATH}  from '../../config'
import { mapActions } from 'vuex'
import { mapGetters } from 'vuex'
import Menu           from './Menu.vue'
import {EventBus}       from '../../eventBus'

export default {
  name: 'Dashboard',
  components: {
    navbar,
    Menu
  },
  data () {
    return {
      username: '',
      email: '',
      pendingRequests: 0,
      requestLoading: false,
      requestInterceptorId: null,
      responseInterceptorId: null,
      loadingDelayTimer: null,
    }
  },
  methods: {
		...mapActions([
			'retrieveToken',
			'logout'
		]),
		handleLoginFormSubmit() {
			this.$store.dispatch('retrieveToken', {
				email: this.email,
				password: this.password
			})
		},
    emitMethod() {
      EventBus.$emit('logged-in', 'loggedin');
    },
		beginRequest() {
			this.pendingRequests += 1
			if (this.pendingRequests !== 1 || this.requestLoading || this.loadingDelayTimer) return
			this.loadingDelayTimer = window.setTimeout(() => {
				this.loadingDelayTimer = null
				if (this.pendingRequests > 0) this.requestLoading = true
			}, 120)
		},
		endRequest() {
			this.pendingRequests = Math.max(0, this.pendingRequests - 1)
			if (this.pendingRequests > 0) return
			if (this.loadingDelayTimer) {
				window.clearTimeout(this.loadingDelayTimer)
				this.loadingDelayTimer = null
			}
			this.requestLoading = false
		},
		installLoadingInterceptors() {
			this.requestInterceptorId = axios.interceptors.request.use(
				(config) => {
					this.beginRequest()
					return config
				},
				(error) => Promise.reject(error)
			)
			this.responseInterceptorId = axios.interceptors.response.use(
				(response) => {
					this.endRequest()
					return response
				},
				(error) => {
					this.endRequest()
					return Promise.reject(error)
				}
			)
		},
		removeLoadingInterceptors() {
			if (this.requestInterceptorId !== null) axios.interceptors.request.eject(this.requestInterceptorId)
			if (this.responseInterceptorId !== null) axios.interceptors.response.eject(this.responseInterceptorId)
			if (this.loadingDelayTimer) window.clearTimeout(this.loadingDelayTimer)
			this.loadingDelayTimer = null
			this.requestLoading = false
			this.pendingRequests = 0
		},
		logout() {
			localStorage.removeItem('usertoken')
			axios.post(`${SERVER_PATH}users/auth/logout`)
			.then((response) => {
				console.log(response)
			})
		}
  },
  computed: mapGetters([
    'getToken',
    'loggedIn',
    'getUser'
  ]),
  created () {
    this.installLoadingInterceptors()
    console.log('created')
  },
  beforeDestroy () {
    this.removeLoadingInterceptors()
  }
}
</script>

<style scoped>
::-webkit-scrollbar { 
   display:unset !important; 
}
.dashboard-shell {
  position: relative;
  min-height: 100vh;
  overflow: hidden;
  background: #080a0f;
  color: #cfd2da;
}

.dashboard-background {
  position: fixed;
  inset: 0;
  z-index: 0;
  background:
    linear-gradient(90deg, rgba(4, 6, 10, 0.82), rgba(6, 8, 12, 0.76) 48%, rgba(4, 6, 10, 0.9)),
    linear-gradient(180deg, rgba(8, 10, 15, 0.18), rgba(8, 10, 15, 0.95)),
    url('~@/assets/img/builder-bg.png');
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
}

.main-frame{
  position: relative;
  /* Sits above the fixed background AND above the navbar (.gc-header, z-index:20)
     so full-screen drawers/modals rendered inside the routed page can cover the
     header instead of being painted under it. A child's z-index can't exceed its
     stacking-context parent, so this cap must clear the navbar. */
  z-index: 30;
  display: flex;
  gap: 1.6rem;
  margin-left: auto;
  margin-right: auto;
  max-width: 1500px ;
  padding: 2rem;
  padding-bottom: 25px;
  overflow: visible;
}

.dashboard-menu-component {
  flex: 0 0 230px;
  overflow: hidden;
  width: 230px;
  min-width: 150px;
  min-height: 680px;
  padding-right: 1.25rem;
  border-right: 1px solid rgba(67, 72, 87, 0.86);
}

.page-router-component {
  position: relative;
  flex: 1;
  min-width: 0;
}

.dashboard-loading-indicator {
  position: fixed;
  top: 82px;
  right: 24px;
  z-index: 120;
  display: inline-flex;
  align-items: center;
  gap: 9px;
  min-height: 38px;
  padding: 0 13px 0 10px;
  border: 1px solid rgba(25, 151, 198, 0.48);
  border-radius: 999px;
  background: rgba(9, 13, 21, 0.94);
  color: #d7eef7;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  box-shadow: 0 16px 38px rgba(0, 0, 0, 0.4);
  pointer-events: none;
}

.dashboard-loading-spinner {
  position: relative;
  width: 22px;
  height: 22px;
}

.dashboard-loading-spinner__circle {
  position: absolute;
  inset: 1px;
  box-sizing: border-box;
  border: 2px solid transparent;
  border-radius: 50%;
}

.dashboard-loading-spinner__circle--top {
  border-top-color: #1997c6;
  animation: dashboard-loading-spin 1s linear infinite;
}

.dashboard-loading-spinner__circle--bottom {
  border-bottom-color: #1997c6;
  animation: dashboard-loading-spin 1s linear infinite alternate;
}

.dashboard-loading-fade-enter-active,
.dashboard-loading-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.dashboard-loading-fade-enter,
.dashboard-loading-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

@keyframes dashboard-loading-spin {
  to { transform: rotate(360deg); }
}

@media (prefers-reduced-motion: reduce) {
  .dashboard-loading-spinner__circle { animation-duration: 1.8s; }
  .dashboard-loading-fade-enter-active,
  .dashboard-loading-fade-leave-active { transition: none; }
}

@media screen and (max-width: 1200px){
  .main-frame {
    flex-direction: column;
    gap: 1rem;
    padding: 1.2rem 1.5rem 2rem;
  }

  .dashboard-menu-component {
    flex: 0 0 auto;
    width: 100%;
    min-height: 0;
    padding-right: 0;
    padding-bottom: 0.75rem;
    border-right: 0;
    border-bottom: 1px solid rgba(67, 72, 87, 0.86);
    overflow: visible;
  }

  .page-router-component {
      width: 100% !important;
      margin-top: 0px !important;
      padding-left: unset !important;
  }
}

@media screen and (max-width: 760px){
  .main-frame {
    flex-direction: column;
    gap: 1.1rem;
    padding: 1rem;
  }

  .dashboard-menu-component {
    width: 100%;
    min-height: auto;
    padding-right: 0;
    padding-bottom: 1rem;
    border-right: 0;
    border-bottom: 1px solid rgba(67, 72, 87, 0.86);
  }

  .dashboard-loading-indicator {
    top: 72px;
    right: 12px;
  }
}

</style>
